import { DecimalPipe, NgClass } from '@angular/common';
import {
  Component,
  computed,
  ElementRef,
  HostBinding,
  inject,
  Injector,
  NgZone,
  signal,
  WritableSignal,
  ChangeDetectionStrategy
} from '@angular/core';
import { FormsModule } from '@angular/forms';
import {
  AiFeaturesService,
  AIStore,
  CODE_3_AI_USER_DATA,
  CODE_4_HUB_USER_DATA,
  DefaultDataProcessingFeatures,
  IQBoostViewStore,
  ProcessingFeaturesByRow,
} from '@maximizer/ai/data-access';
import {
  AIFeatures,
  ConfigurationService,
  ContextService,
  FeatureService,
  SessionService,
} from '@maximizer/core/shared/data-access';
import {
  CDN_URL,
  ConfigurationSetting,
  ConfigurationSettingCodes,
} from '@maximizer/core/shared/domain';
import {
  ActionEventType,
  ShowDialogArgs,
} from '@maximizer/core/shared/ui/config';
import { EmbeddedComponent } from '@maximizer/core/shared/ui/components/base';
import { UI_PIPES } from '@maximizer/core/shared/ui/pipes';
import { isNullOrEmpty } from '@maximizer/shared/util';
import { TranslatePipe, TranslateService } from '@ngx-translate/core';
import { KENDO_BUTTON } from '@progress/kendo-angular-buttons';
import { KENDO_SWITCH } from '@progress/kendo-angular-inputs';
import {
  catchError,
  debounceTime,
  distinctUntilChanged,
  finalize,
  forkJoin,
  map,
  Observable,
  of,
  Subject,
  switchMap,
  tap,
} from 'rxjs';
import {
  AiHubEventTracker,
  EventAction,
  EventJourney,
} from '../classes/event-tracker';
import { IQBoostInternalCommand } from '../classes/iqboost';
import {
  AiFeatureApp,
  AiFeatureGroupItem,
  AiFeatureItem,
  AiHubUserData,
  AnonymizerSwitchValues,
} from './models/ai-hub-classic.models';
import { Labels } from './models/labels.model';

@Component({
  selector: 'ai-hub-classic',
  templateUrl: './ai-hub-classic.component.html',
  styleUrl: './ai-hub-classic.component.scss',
  providers: [AiHubEventTracker],
  changeDetection: ChangeDetectionStrategy.Eager,
  imports: [
    DecimalPipe,
    FormsModule,
    KENDO_SWITCH,
    KENDO_BUTTON,
    NgClass,
    TranslatePipe,
    UI_PIPES,
  ],
})
export class AiHubClassicComponent extends EmbeddedComponent {
  @HostBinding('class') hostClasses = 'ai-ng';

  static readonly aiHubUserDataDefault: AiHubUserData = {
    dataIsSecureExpanded: false,
  };

  readonly aiHubUserDataSettingsCodes: ConfigurationSettingCodes = {
    code3: CODE_3_AI_USER_DATA,
    code4: CODE_4_HUB_USER_DATA,
  };

  private _labels?: Labels;
  private readonly saveUserData$ = new Subject<AiHubUserData>();

  aiHubUserDataSetting: ConfigurationSetting | null = null;

  loading = signal(false);
  isAiEnabled = signal(false);
  isAnonymizerEnabled = signal(false);
  isAnonymizerEnabledByAdmin = signal(false);
  isFFAnonymizerEnabled = signal(true);
  aiFeaturesGroup: WritableSignal<AiFeatureGroupItem[]> = signal([]);
  noteDataIsSecureExpanded = signal(false);
  isAdmin = signal(false);

  creditsProgressPercentage = computed(() => {
    const used = this.aiStore.aiUserDataGroup().admin.creditsUsed;
    const total = this.aiStore.aiUserDataGroup().admin.creditsTotal;
    const percentage = total > 0 ? (used / total) * 100 : 0;
    return percentage;
  });
  isSLE = computed(() => this.aiFeaturesService.isSLE());
  creditsUsed = computed(
    () => this.aiStore.aiUserDataGroup().admin.creditsUsed,
  );
  creditsTotal = computed(
    () => this.aiStore.aiUserDataGroup().admin.creditsTotal,
  );
  loadingGifUrl = computed(
    () => `${this.context.integration?.url ?? ''}/resources/ai-loading.gif`,
  );
  isAnonymizerToggleDisabled = computed(
    () =>
      !this.isFFAnonymizerEnabled() ||
      !this.isAiEnabled() ||
      (!this.isAdmin() && this.isAnonymizerEnabledByAdmin()) ||
      this.loading(),
  );

  private readonly ngZone = inject(NgZone);
  private readonly injector = inject(Injector);
  private readonly session = inject(SessionService);
  private readonly feature = inject(FeatureService);
  private readonly context = inject(ContextService);
  private readonly aiFeaturesService = inject(AiFeaturesService);
  private readonly configurationService = inject(ConfigurationService);
  public readonly aiStore = inject(AIStore);
  private readonly iqBoostStore = inject(IQBoostViewStore);
  private readonly tracker = inject(AiHubEventTracker);
  readonly cdn = inject(CDN_URL);

  constructor() {
    super(inject(ElementRef));

    this.isFFAnonymizerEnabled.set(
      this.feature.isFeatureOn('core-ai-anonymizer-enabled', true),
    );

    this.addSubscription(
      this.saveUserData$
        .pipe(
          debounceTime(400),
          distinctUntilChanged(
            (previous, current) =>
              previous.dataIsSecureExpanded === current.dataIsSecureExpanded,
          ),
          tap(() => this.loading.set(true)),
          switchMap((userData) =>
            this.saveAiHubUserData(userData).pipe(
              finalize(() => this.loading.set(false)),
            ),
          ),
        )
        .subscribe(),
    );
  }

  override doAction(name: string, args?: unknown): void {
    this.ngZone.run(() => {
      if (name === ActionEventType.Initialize) {
        if (args) this.initialize();
      } else {
        super.doAction(name, args);
      }
    });
  }

  private initialize(): void {
    this.loading.set(true);
    this.tracker.trackEvent(EventJourney.Action, EventAction.Opened);

    forkJoin({
      labels: this.loadLabels(),
      session: this.session.getInfo(),
    })
      .pipe(
        tap(({ labels }) => (this._labels = labels)),
        switchMap(({ session }) => {
          this.aiHubUserDataSettingsCodes.code2 = session.user.id;
          this.iqBoostStore.configurationSettingCodesUser().code2 = session.user.id;
          this.isAdmin.set(session.user.roles.administrator);
          return forkJoin({
            config: this.aiFeaturesService.getConfiguration(session),
            userData: this.getAiHubUserData(),
            anonymizerConfig: this.getAnonymizerConfiguration(),
          });
        }),
        tap(({ config, userData, anonymizerConfig }) => {
          this.noteDataIsSecureExpanded.set(userData.dataIsSecureExpanded);
          this.isAiEnabled.set(config.enabled);
          this.applyAnonymizerConfiguration(anonymizerConfig);
          this.aiStore.setUserDataGroup(config.aiUserDataGroup);
          this.initializeFeatures();
        }),
        finalize(() => this.loading.set(false)),
      )
      .subscribe();
  }

  private getAnonymizerConfiguration(): Observable<ProcessingFeaturesByRow> {
    return this.aiFeaturesService.getProcessingFeatures().pipe(
      catchError(() =>
        of({
          admin: DefaultDataProcessingFeatures,
          user: DefaultDataProcessingFeatures,
        }),
      ),
    );
  }

  private applyAnonymizerConfiguration(
    anonymizerConfig: ProcessingFeaturesByRow,
  ): void {
    this.isAnonymizerEnabledByAdmin.set(anonymizerConfig.admin.anonymized);

    if (this.isAdmin()) {
      this.isAnonymizerEnabled.set(anonymizerConfig.admin.anonymized);
    } else {
      this.isAnonymizerEnabled.set(anonymizerConfig.user.anonymized || anonymizerConfig.admin.anonymized);
    }
  }

  private loadLabels(): Observable<Labels> {
    return this.translateAfterIntegrationLoads().get(['aiHub']);
  }

  private translateAfterIntegrationLoads(): TranslateService {
    return this.injector.get(TranslateService);
  }

  private initializeFeatures(): void {
    if (!this._labels) return;

    this.aiFeaturesGroup.set([]);

    const userData = this.aiStore.aiUserDataGroup().user.aiFeaturesEnabled;
    const adminData = this.aiStore.aiUserDataGroup().admin.aiFeaturesEnabled;

    this.addFeature({
      enabled: userData.iqBoost && adminData.iqBoost,
      enabledAdmin: adminData.iqBoost,
      title: this._labels.aiHub.features.iqBoost.title,
      description: this.getVersionedContent(
        this._labels.aiHub.features.iqBoost.description,
      ),
      location: this.getVersionedContent(
        this._labels.aiHub.features.iqBoost.location,
      ),
      data: this.getVersionedContent(this._labels.aiHub.features.iqBoost.data),
      limitation: this._labels.aiHub.features.iqBoost.limitation,
      aiFeaturePropertyEnabled: 'iqBoost',
      app: {
        title: this._labels.aiHub.features.iqBoost.title,
        tag: 'maximizer-ai-iqboost',
        featureFlagId: 'core-ai-iq-boost-menu-shown',
      },
      featureFlagId: 'core-ai-iq-boost-shown',
      featureFlagDefault: false,
      imgSrc: `${this.cdn}/assets/images/previews/ai-hub/preview-iqboost-new.png`,
      iconClass: 'maximizer-icons icon-iq-boost icon-22 ml-2 mr-[-0.25rem]',
    });

    this.addFeature({
      enabled: userData.iqBoostWidget && adminData.iqBoostWidget,
      enabledAdmin: adminData.iqBoostWidget,
      title: this._labels.aiHub.features.iqBoostWidget.title,
      description: this.getVersionedContent(
        this._labels.aiHub.features.iqBoostWidget.description,
      ),
      location: this.getVersionedContent(
        this._labels.aiHub.features.iqBoostWidget.location,
      ),
      data: this.getVersionedContent(
        this._labels.aiHub.features.iqBoostWidget.data,
      ),
      limitation: this._labels.aiHub.features.iqBoostWidget.limitation,
      aiFeaturePropertyEnabled: 'iqBoostWidget',
      featureFlagId: 'core-ai-iq-boost-widget-shown',
      featureFlagDefault: false,
      imgSrc: `${this.cdn}/assets/images/previews/ai-hub/preview-iqboost-inline-summary.png`,
      iconClass:
        'maximizer-icons icon-inline-summary icon-22 ml-2 mr-[-0.25rem]',
    });

    this.addFeature({
      enabled: userData.summarization && adminData.summarization,
      enabledAdmin: adminData.summarization,
      title: this._labels.aiHub.features.summarization.title,
      description: this.getVersionedContent(
        this._labels.aiHub.features.summarization.description,
      ),
      location: this.getVersionedContent(
        this._labels.aiHub.features.summarization.location,
      ),
      data: this.getVersionedContent(
        this._labels.aiHub.features.summarization.data,
      ),
      limitation: this._labels.aiHub.features.summarization.limitation,
      aiFeaturePropertyEnabled: 'summarization',
      featureFlagId: 'core-timeline-ai-summarization-shown',
      featureFlagOrganizationId: 'core-ai-anonymizer-enabled',
      featureFlagDefault: false,
      imgSrc: `${this.cdn}/assets/images/previews/ai-hub/preview-timeline-summary-new.png`,
      iconClass:
        'maximizer-icons icon-timeline-summary icon-22 ml-2 mr-[-0.25rem]',
    });

    this.addFeature({
      enabled: userData.meetingPreparation && adminData.meetingPreparation,
      enabledAdmin: adminData.meetingPreparation,
      title: this._labels.aiHub.features.meetingPreparation.title,
      description: this.getVersionedContent(
        this._labels.aiHub.features.meetingPreparation.description,
      ),
      location: this.getVersionedContent(
        this._labels.aiHub.features.meetingPreparation.location,
      ),
      data: this.getVersionedContent(
        this._labels.aiHub.features.meetingPreparation.data,
      ),
      limitation: this._labels.aiHub.features.meetingPreparation.limitation,
      aiFeaturePropertyEnabled: 'meetingPreparation',
      featureFlagId: 'core-ai-meeting-preparation-summary-shown',
      featureFlagOrganizationId: 'core-ai-anonymizer-enabled',
      featureFlagDefault: false,
      imgSrc: `${this.cdn}/assets/images/previews/ai-hub/preview-meeting-preparation-new.png`,
      iconClass:
        'maximizer-icons icon-meeting-preparation icon-22 ml-2 mr-[-0.25rem]',
    });

    this.addFeature({
      enabled: userData.smartCapture && adminData.smartCapture,
      enabledAdmin: adminData.smartCapture,
      title: this._labels.aiHub.features.mobileSmartCapture.title,
      description: this.getVersionedContent(
        this._labels.aiHub.features.mobileSmartCapture.description,
      ),
      location: this.getVersionedContent(
        this._labels.aiHub.features.mobileSmartCapture.location,
      ),
      data: this.getVersionedContent(
        this._labels.aiHub.features.mobileSmartCapture.data,
      ),
      limitation: this._labels.aiHub.features.mobileSmartCapture.limitation,
      aiFeaturePropertyEnabled: 'smartCapture',
      featureFlagId: 'mobile-smartcapture-shown',
      featureFlagDefault: false,
      imgSrc: `${this.cdn}/assets/images/previews/ai-hub/preview-mobile-smart-capture.png`,
      iconClass:
        'maximizer-icons icon-smart-capture icon-22 ml-2 mr-[-0.25rem]',
    });

    this.addFeature({
      enabled: userData.outlook && adminData.outlook,
      enabledAdmin: adminData.outlook,
      title: this._labels.aiHub.features.outlook.title,
      description: this.getVersionedContent(
        this._labels.aiHub.features.outlook.description,
      ),
      location: this.getVersionedContent(
        this._labels.aiHub.features.outlook.location,
      ),
      data: this.getVersionedContent(this._labels.aiHub.features.outlook.data),
      limitation: this._labels.aiHub.features.outlook.limitation,
      aiFeaturePropertyEnabled: 'outlook',
      featureFlagId: 'integration-o365-ai-features-enabled',
      featureFlagDefault: false,
      imgSrc: `${this.cdn}/assets/images/previews/ai-hub/preview-outlook-autofill.png`,
      iconClass: 'fa-icons icon-envelope icon-20 ml-2',
    });
  }

  private getVersionedContent(content: {
    faVersion: string;
    defaultVersion: string;
  }): string {
    return this.isSLE() ? content.defaultVersion : content.faVersion;
  }

  private addFeature(featureItem: AiFeatureItem, category?: string): void {
    if (
      featureItem.featureFlagId &&
      featureItem.featureFlagDefault !== undefined &&
      !this.feature.isFeatureOn(
        featureItem.featureFlagId,
        featureItem.featureFlagDefault,
      )
    ) {
      return;
    }

    let group = this.aiFeaturesGroup().find(
      (existingGroup) => existingGroup.category === category,
    );

    if (!group) {
      group = {
        category,
        items: [],
      };
      this.aiFeaturesGroup().push(group);
    }

    if (
      featureItem.app?.featureFlagId &&
      !this.feature.isFeatureOn(featureItem.app.featureFlagId, false)
    ) {
      featureItem.app = undefined;
    }

    group.items.push(featureItem);
  }

  openFeature(app: AiFeatureApp): void {
    const dialogArgs = new ShowDialogArgs(app.tag, '');
    if (app.args) {
      Object.assign(dialogArgs, app.args);
    }
    this.actionCompleted.emit(dialogArgs);
  }

  onFeatureToggle(feature: AiFeatureItem): void {
    const featureKey = feature.aiFeaturePropertyEnabled;
    if (featureKey) {
      this.loading.set(true);

      const saves = [
        this.aiFeaturesService.setUserFeature(featureKey, feature.enabled),
      ];
      if (this.aiStore.aiUserDataGroup().isAdmin) {
        saves.push(
          this.aiFeaturesService.setOrganizationFeature(
            featureKey,
            feature.enabled,
          ),
        );
      }

      forkJoin(saves)
        .pipe(
          map((results) => results.every(Boolean)),
          tap((saved) => {
            if (saved) {
              this.tracker.trackEvent(
                EventJourney.Action,
                EventAction.Changed,
                {
                  mode: feature.enabled ? 'enabled' : 'disabled',
                  feature: featureKey,
                },
              );
            } else {
              this.realignFeature(feature, featureKey);
            }
          }),
          finalize(() => this.loading.set(false)),
        )
        .subscribe();
    }
  }

  private realignFeature(
    feature: AiFeatureItem,
    featureKey: keyof AIFeatures,
  ): void {
    const { user, admin } = this.aiStore.aiUserDataGroup();
    feature.enabled =
      user.aiFeaturesEnabled[featureKey] && admin.aiFeaturesEnabled[featureKey];
    feature.enabledAdmin = admin.aiFeaturesEnabled[featureKey];
  }

  isFeatureEnabled(feature: AiFeatureItem): boolean {
    return feature.enabledAdmin || this.aiStore.aiUserDataGroup().isAdmin;
  }

  isFeatureEnabledByOrganization(feature: AiFeatureItem): boolean {
    if (isNullOrEmpty(feature.featureFlagOrganizationId)) {
      return true;
    }
    return (
      !!feature.featureFlagOrganizationId &&
      this.feature.isFeatureOn(feature.featureFlagOrganizationId, false)
    );
  }

  setAiEnabled(enabled: boolean): void {
    this.loading.set(true);
    this.isAiEnabled.set(enabled);
    this.tracker.trackEvent(
      EventJourney.Action,
      enabled ? EventAction.Enabled : EventAction.Disabled,
    );

    this.addSubscription(
      this.aiFeaturesService
        .setEnabled(enabled)
        .pipe(finalize(() => this.loading.set(false)))
        .subscribe(),
    );

    const launcher = top?.document?.getElementById(
      'ai-iqboost-launcher',
    ) as HTMLElement & EmbeddedComponent;
    launcher?.doAction(IQBoostInternalCommand.SetEnabled, enabled);
  }

  toggleTrustAndSafety(): void {
    this.noteDataIsSecureExpanded.update((expanded) => !expanded);
    if (this.aiFeaturesGroup().length > 0) {
      this.saveUserData$.next({
        dataIsSecureExpanded: this.noteDataIsSecureExpanded(),
      });
    }
  }

  setAnonymizerEnabled(enabled: boolean): void {
    const switchBeforeClick: AnonymizerSwitchValues = {
      enabled: this.isAnonymizerEnabled(),
      enabledByAdmin: this.isAnonymizerEnabledByAdmin(),
    };
    this.loading.set(true);
    this.isAnonymizerEnabled.set(enabled);
    if (this.isAdmin()) {
      this.isAnonymizerEnabledByAdmin.set(enabled);
    }

    const saves = [
      this.aiFeaturesService.setUserProcessingFeatures({ anonymized: enabled }),
    ];
    if (this.isAdmin()) {
      saves.push(
        this.aiFeaturesService.setOrganizationProcessingFeatures({
          anonymized: enabled,
        }),
      );
    }

    this.addSubscription(
      forkJoin(saves)
        .pipe(
          map((results) => results.every(Boolean)),
          switchMap((success) =>
            success
              ? of(success)
              : this.realignAnonymizer(switchBeforeClick).pipe(
                  map(() => success),
                ),
          ),
          finalize(() => this.loading.set(false)),
        )
        .subscribe((success) => {
          if (!success) {
            return;
          }

          this.iqBoostStore.setAnonymizedInAnimation({
            animate: true,
            anonymized: enabled,
          });
          setTimeout(() => {
            this.iqBoostStore.setAnonymizedInAnimation({
              animate: false,
              anonymized: enabled,
            });
          }, 1500);
        }),
    );
  }

  private realignAnonymizer(
    switchBeforeClick: AnonymizerSwitchValues,
  ): Observable<void> {
    return this.aiFeaturesService.getProcessingFeatures().pipe(
      map((anonymizerConfig) =>
        this.applyAnonymizerConfiguration(anonymizerConfig),
      ),
      catchError(() => {
        this.isAnonymizerEnabled.set(switchBeforeClick.enabled);
        this.isAnonymizerEnabledByAdmin.set(switchBeforeClick.enabledByAdmin);
        return of(undefined);
      }),
    );
  }

  private getAiHubUserData(): Observable<AiHubUserData> {
    return this.configurationService
      .getSetting(this.aiHubUserDataSettingsCodes)
      .pipe(
        map((setting) => {
          this.aiHubUserDataSetting = setting;
          return setting?.textValue
            ? (JSON.parse(setting.textValue) as AiHubUserData)
            : AiHubClassicComponent.aiHubUserDataDefault;
        }),
        catchError(() => of(AiHubClassicComponent.aiHubUserDataDefault)),
      );
  }

  private saveAiHubUserData(userData: AiHubUserData): Observable<boolean> {
    const currentSetting = this.aiHubUserDataSetting
      ? of(this.aiHubUserDataSetting)
      : this.configurationService.getSetting(this.aiHubUserDataSettingsCodes);

    return currentSetting.pipe(
      switchMap((response) =>
        this.configurationService.updateSetting(
          response?.id ?? null,
          this.aiHubUserDataSettingsCodes,
          JSON.stringify(userData),
        ),
      ),
      tap((setting) => (this.aiHubUserDataSetting = setting)),
      catchError(() => of(null)),
      map((setting) => (setting?.id?.length ?? 0) > 0),
    );
  }

  override ngOnDestroy(): void {
    this.tracker.trackEvent(EventJourney.Action, EventAction.Closed);
    super.ngOnDestroy();
  }
}
